import React, { useEffect, useRef, useState } from "react";
import {
  Users,
  Dumbbell,
  Camera,
  Plane,
  Smartphone,
  DoorOpen,
  Hand,
  UserX,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const iconMap = {
  users: Users,
  dumbbell: Dumbbell,
  camera: Camera,
  plane: Plane,
  smartphone: Smartphone,
  "door-open": DoorOpen,
  hand: Hand,
  "user-x": UserX,
};

const RiskAreasSection = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setIsVisible(true),
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const items = t("riskAreas.items") || [];

  return (
    <section id="risks" ref={sectionRef} className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div
          className={`mb-12 max-w-4xl transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="inline-flex items-center justify-center w-14 h-14 bg-red-100 rounded-xl mb-6">
            <AlertTriangle className="w-7 h-7 text-red-600" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a2e] mb-4">
            {t("riskAreas.title")}
          </h2>
          <div className="w-20 h-1 bg-[#E8792B] rounded-full mb-6" />
          <p className="text-gray-600 text-base leading-relaxed">{t("riskAreas.intro")}</p>
        </div>

        {/* GXR-style cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {Array.isArray(items) &&
            items.map((item, index) => {
              const Icon = iconMap[item.icon] || AlertTriangle;
              const num = String(index + 1).padStart(2, "0");
              return (
                <div
                  key={index}
                  className={`group relative overflow-hidden p-8 lg:p-9 bg-white border border-gray-200 hover:border-[#E8792B]/50 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-500 ${
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                  }`}
                  style={{ transitionDelay: `${index * 80}ms` }}
                >
                  {/* Large faded number watermark */}
                  <div className="absolute -bottom-8 -right-2 font-black text-[140px] lg:text-[180px] leading-none text-[#E8792B]/[0.07] pointer-events-none select-none">
                    {num}
                  </div>

                  {/* Top hairline accent that grows on hover */}
                  <span className="absolute top-0 left-0 h-px w-12 bg-[#E8792B] transition-all duration-500 group-hover:w-full" />

                  {/* Square icon tile */}
                  <div className="relative inline-flex items-center justify-center h-12 w-12 border border-[#E8792B]/40 bg-[#E8792B]/5 transition-all duration-300 group-hover:bg-[#E8792B]/10 group-hover:border-[#E8792B] group-hover:shadow-[0_0_24px_rgba(232,121,43,0.25)]">
                    <Icon className="h-5 w-5 text-[#E8792B]" strokeWidth={2} />
                  </div>

                  <div className="relative mt-8 text-[11px] font-bold tracking-[0.3em] text-[#E8792B]/70">
                    · {num} ·
                  </div>

                  <h3 className="relative mt-4 text-[18px] lg:text-[20px] font-bold leading-[1.2] text-[#1a1a2e] tracking-tight">
                    {item.title}
                  </h3>

                  <p className="relative mt-4 text-gray-600 text-[14px] leading-relaxed max-w-[36ch]">
                    {item.text}
                  </p>

                  {/* Bottom arrow reveal on hover */}
                  <div className="relative mt-6 flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-transparent group-hover:text-[#E8792B] transition-all duration-300">
                    <span className="h-px w-6 bg-[#E8792B] transition-all duration-300 group-hover:w-10" />
                    <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </section>
  );
};

export default RiskAreasSection;
